import {
  Checkbox,
  Datagrid,
  Modal,
  Button,
  Stack,
  Spinner
} from '@efast_public/fjd-component-library';
import React, { useEffect, useState } from 'react';
import { useUpdateCase } from '../../api/hooks/useUpdateCase';
import { FormatUtil } from '../../shared/utils/formatUtil';
import styles from '../../pages/Schreibtisch/SchreibtischBody/Dokumente/dokumente.module.css';
import { Bescheidstatus } from '../../api/models/Status/Bescheidstatus';
import BescheidstatusBadge from '../../pages/shared/BescheidstatusBadge/BescheidstatusBadge';
import AntragBadge from '../../pages/shared/AntragBadge/AntragBadge';
import { formatByte } from '../../shared/utils/antragsuebersicht';
import { useQuery } from '@tanstack/react-query';
import {
  AttachmentExtendedModel,
  CaseDetailsModel
} from '../../api/models/Case/CaseDetailsModel';
import { AxiosError } from 'axios';
import { BehApiRoutes } from '../../api/beh-api';
import useCheckboxes, {
  ICheckboxesHook
} from '../../shared/hooks/useCheckboxes';

interface AntragDownloadModalProps {
  readonly onClose: () => void;
  readonly caseId: string;
  readonly auftragsnummer: string;
  readonly isOpen: boolean;
}

export function AntragDownloadModal({
  caseId,
  ...props
}: AntragDownloadModalProps) {
  const { downloadSummary } = useUpdateCase();

  const [queryParameter, setQueryParameter] = useState<string | undefined>(
    undefined
  );

  const { data: caseDetails, error: caseDetailsError } = useQuery<
    CaseDetailsModel,
    AxiosError
  >({
    queryKey: [BehApiRoutes.case(caseId)]
  });
  const {
    data: attachments,
    isLoading,
    error: attachmentsError
  } = useQuery<Array<AttachmentExtendedModel>, AxiosError>({
    queryKey: [BehApiRoutes.caseAttachments(caseId)]
  });

  const {
    getIdsAsParam,
    getCheckBoxStateById,
    setCheckBoxState,
    checkBoxState,
    selectAllCheckBox,
    onSelectAllCheckboxes,
    onClickCheckBox
  }: ICheckboxesHook = useCheckboxes();

  const initialSubmissionId = caseDetails?.initialSubmission.submissionId;

  const hasError = attachmentsError || caseDetailsError;

  const handleDownload = () => {
    const timestamp = FormatUtil.fileDateTimeFormatted();
    downloadSummary({
      caseId,
      filename: `${props.auftragsnummer}_${timestamp}_Download.zip`,
      attachmentIds: queryParameter
    }).then(() => props.onClose());
  };

  useEffect(() => {
    setCheckBoxState([]);
    if (attachments) {
      setCheckBoxState(
        attachments.map((att) => {
          return {
            checkboxId: `${att.description}_checkbox`,
            id: att.id,
            checked: true
          };
        })
      );
      return () => {
        setCheckBoxState([]);
      };
    }
  }, [caseDetails, setCheckBoxState, attachments]);

  const documents = attachments
    ? attachments.map((document) => {
        return { ...document, isBescheid: document.isBescheid };
      })
    : [];

  return (
    <Modal
      appElement={document.querySelector('.fjd-base-layout') as HTMLElement}
      closeOnBackdropClick={false}
      open={props.isOpen}
      windowSize="l"
      onClose={props.onClose}
      data-testid="antrag-download-modal"
    >
      <Modal.Header id="AntragDownloadModal-header">
        Antrag herunterladen
      </Modal.Header>
      <Modal.Content>
        <Stack direction="column">
          <div>
            Sie haben die Möglichkeit, neben der Zusammenfassung des aktuellen
            Bearbeitungsstandes zusätzlich die Dokumente des Antrags
            herunterzuladen. Diese können Sie hier an- oder abwählen.
          </div>
            <Datagrid<(typeof documents)[number]>
              columns={[
                {
                  id: 'select',
                  header: '',
                  accessorKey: 'id',
                  enableSorting: false,
                  enableResizing: false,
                  enableHiding: false,
                  size: 50,
                  minSize: 40,
                  cell: (row) => (
                    <Checkbox
                      checked={
                        getCheckBoxStateById(row.getValue<string>()).checked
                      }
                      id={
                        getCheckBoxStateById(row.getValue<string>()).checkboxId
                      }
                      onChange={(value) =>
                        onClickCheckBox(
                          value?.currentTarget.checked,
                          row.getValue<string>()
                        )
                      }
                    />
                  )
                },
                {
                  id: 'name',
                  header: 'Name',
                  accessorKey: 'description',
                  enableHiding: false,
                  enableSorting: true,
                  enableResizing: true,
                  sortingFn: 'auto',
                  cell: (row) => (
                    <Stack
                      direction="row"
                      spacing="xl"
                      className={styles.container}
                    >
                      <div style={{ whiteSpace: 'initial' }}>
                        {row.row.original.description +
                          row.row.original.filetype}
                      </div>
                      {row.row.original.isBescheid &&
                        (caseDetails?.bescheidstatus ===
                          Bescheidstatus.POSITIV ||
                          caseDetails?.bescheidstatus ===
                            Bescheidstatus.NEGATIV) && (
                          <BescheidstatusBadge
                            bescheidstatus={caseDetails?.bescheidstatus}
                            fjdBadgeSize={'xs'}
                            badgeLabel={'Bescheid'}
                          />
                        )}
                      {row.row.original.submissionId ===
                        initialSubmissionId && (
                        <AntragBadge data-testid="antragBadge" />
                      )}
                    </Stack>
                  )
                },
                {
                  id: 'filetype',
                  header: 'Typ',
                  accessorKey: 'filetype',
                  enableHiding: false,
                  size: 100,
                  minSize: 80,
                  enableSorting: true,
                  enableResizing: true,
                  sortingFn: 'auto',
                  cell: (row) => (
                    <span>{row.getValue<string>().toUpperCase()}</span>
                  )
                },
                {
                  id: 'size',
                  header: 'Größe',
                  accessorKey: 'size',
                  enableHiding: false,
                  size: 100,
                  minSize: 80,
                  enableSorting: true,
                  enableResizing: true,
                  sortingFn: 'auto',
                  cell: (row) => (
                    <span>{formatByte(`${row.getValue<string>()}`)}</span>
                  )
                }
              ]}
              data={documents}
              dataKey="id"
              isDataLoading={isLoading}
              multiSelect={true}
              areColumnsHideable={false}
              onMultiSelectionChange={(multiSelection) => {
                setQueryParameter(multiSelection.map((s) => s.id).join(','));
              }}
              areColumnsResizeable
              manualPagination={false}
              manualSorting={false}
              labels={{
                currentPage: 'Aktuelle Seite',
                detailButton: 'Weitere Infos ein-/ausblenden',
                emptyData: 'Keine Dokumente gefunden.',
                nextPage: 'Nächste Seite',
                of: 'Seite {0} von {1}',
                page: 'Seite',
                pageSize: '{0} Einträge pro Seite',
                previousPage: 'Vorherige Seite',
                rows: 'Zeilen',
                tableTitle: '',
                ascBtn: 'Aufsteigend sortieren',
                descBtn: 'Absteigend sortieren',
                hideBtn: 'Spalte ausblenden'
              }}
            />
        </Stack>
      </Modal.Content>
      <Modal.Footer>
        {hasError ? (
          <Button variant="primary" onClick={props.onClose}>
            Schließen
          </Button>
        ) : (
          <>
            <Button variant="secondary-outline" onClick={props.onClose}>
              Abbrechen
            </Button>
            <Button variant="primary" onClick={handleDownload}>
              Antrag herunterladen (ZIP-Datei)
            </Button>
          </>
        )}
      </Modal.Footer>
    </Modal>
  );
}

export default AntragDownloadModal;
